/**
 * Component for rendering Markdown content with proper styling
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../../lib/utils';

interface MarkdownDisplayProps {
  content: string;
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Renders markdown content with consistent styling.
 * Use this for displaying AI-generated content, notes, and other markdown text.
 */
export function MarkdownDisplay({ content, variant = 'default', className }: MarkdownDisplayProps) {
  const isCompact = variant === 'compact';

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        isCompact && 'prose-p:mb-2 prose-headings:mt-2 prose-headings:mb-1 prose-h1:text-base prose-h2:text-sm prose-h3:text-sm',
        !isCompact && 'prose-p:mb-3 prose-headings:mt-4 prose-headings:mb-2',
        'prose-a:text-primary prose-a:underline',
        'prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-mono prose-code:text-[0.875em]',
        'prose-pre:rounded-lg prose-pre:bg-muted prose-pre:p-4',
        'prose-blockquote:border-l-4 prose-blockquote:border-border prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground',
        'prose-hr:border-border',
        'prose-table:w-full prose-table:border-collapse',
        'prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-1.5 prose-th:text-start prose-th:font-semibold',
        'prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-1.5 prose-td:text-start',
        className
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}
